import React, { useEffect, useState } from "react";
import {
  getLeads,
  createLead,
  updateLeadStage,
  getAllOperators,
  getConfig,
  getUsers,
} from "../../services/api";
import globalStyles, { COLORS } from "../../styles/globalStyles";
import { getPageNumbers } from "../../helpers/Helpers";

import { FiEdit, FiEye, FiSearch } from "react-icons/fi";

import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PRIMARY_COLOR = "#534AB7";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const DEFAULT_STAGES = [
  "prospecting",
  "contacted",
  "demo_scheduled",
  "demo_done",
  "negotiation",
  "closed_won",
  "closed_lost",
];

const DEFAULT_STATUSES = {
  prospecting: ["pending", "in_progress"],
  contacted: ["pending", "in_progress", "no_response"],
  demo_scheduled: ["pending", "confirmed", "cancelled"],
  demo_done: ["pending", "completed"],
  negotiation: ["pending", "in_progress", "on_hold"],
  closed_won: ["confirmed"],
  closed_lost: ["confirmed"],
};

const stageIcons = {
  prospecting: PendingActionsIcon,
  contacted: ContactPhoneIcon,
  demo_scheduled: EventAvailableIcon,
  demo_done: FactCheckIcon,
  negotiation: HandshakeIcon,
  closed_won: CheckCircleIcon,
  closed_lost: CheckCircleIcon,
};

const stageColors = {
  prospecting: "#8b5cf6",
  contacted: "#0284c7",
  demo_scheduled: "#2563eb",
  demo_done: "#059669",
  negotiation: "#d97706",
  closed_won: "#10b981",
  closed_lost: "#ef4444",
};

const s = {
  tilesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
    gap: 16,
    marginBottom: 18,
    width: "100%",
  },

  tileGridItem: () => ({
    gridColumn: "span 3",
  }),

  timelineItem: {
    padding: "8px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    marginBottom: 8,
  },

  timelineText: {
    fontSize: 12,
    color: "#475569",
    marginTop: 4,
  },

  seeMoreBtn: {
    marginTop: 6,
    border: "none",
    background: "transparent",
    color: PRIMARY_COLOR,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    padding: 0,
  },
};

const LeadTile = ({ value, label, icon: Icon, color, active, onClick }) => (
  <div
    style={{ ...globalStyles.tile, ...(active ? globalStyles.activeTile : {}) }}
    onClick={onClick}
  >
    <div style={globalStyles.tileIcon(color)}>
      <Icon fontSize="medium" />
    </div>

    <div style={globalStyles.tileText}>
      <div style={globalStyles.tileValue}>{value ?? 0}</div>
      <div style={globalStyles.tileLabel}>{label}</div>
    </div>
  </div>
);

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);

  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [stageModal, setStageModal] = useState(null);
  const [view, setView] = useState(null);

  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});
  const isMobile = useIsMobile();

  const [form, setForm] = useState({
    operatorId: "",
    currentStage: "prospecting",
    status: "pending",
    assignedTo: "",
  });

  const [stageForm, setStageForm] = useState({
    stage: "",
    status: "",
    note: "",
  });

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);

  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    currentStage: "",
    status: "",
  });

  const [showAllTimeline, setShowAllTimeline] = useState(false);
  const [leadCounts, setLeadCounts] = useState({
    total: 0,
    stages: {},
  });

  const getStatusOptions = (stage) => {
    return statuses?.[stage] || [];
  };

  const allStatuses = filters.currentStage
    ? getStatusOptions(filters.currentStage)
    : Object.values(statuses)
        .flat()
        .filter((item, index, arr) => arr.indexOf(item) === index);

  const formatLabel = (value) => {
    if (!value) return "—";

    return String(value)
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getPersonName = (value) => {
    if (!value) return "—";
    if (typeof value === "string") return value;
    return value.name || value.email || value.employeeId || "—";
  };

  const getTimelineItems = (lead) => {
    if (Array.isArray(lead?.timeline)) return lead.timeline;
    if (Array.isArray(lead?.timelineDetails)) return lead.timelineDetails;
    if (Array.isArray(lead?.timeline_details)) return lead.timeline_details;
    if (Array.isArray(lead?.history)) return lead.history;
    return [];
  };

  const clearFieldError = (key) => {
    setFieldErr((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  const getFieldError = (key) => {
    const error = fieldErr[key];

    if (Array.isArray(error)) return error[0];

    if (typeof error === "object" && error?.message) {
      return error.message;
    }

    return error || "";
  };

  const getInputStyle = (key, baseStyle = globalStyles.select) => ({
    ...baseStyle,
    ...(getFieldError(key) ? globalStyles.inputError : {}),
  });

  const setApiErrors = (error, fallbackMessage) => {
    const apiError = error?.response?.data;
    const apiErrors = apiError?.errors || {};

    setFieldErr(apiErrors);

    if (Object.keys(apiErrors).length > 0) {
      setErr("");
    } else {
      setErr(apiError?.message || fallbackMessage);
    }
  };

  const load = () => {
    setLoading(true);

    Promise.all([
      getLeads({
        page,
        limit,
        search,
        currentStage: filters.currentStage,
        status: filters.status,
      }),
      getLeads({ limit: 1000 }),
      getAllOperators({ limit: 500 }),
      getConfig(),
      getUsers({ limit: 500 }),
    ])
      .then(([leadRes, allLeadRes, operatorRes, configRes, userRes]) => {
        const leadData = leadRes?.data?.data || {};

        const apiLeads = Array.isArray(leadData.leads) ? leadData.leads : [];

        setTotal(leadData.total || 0);
        setTotalPages(leadData.totalPages || 1);
        setIsLastPage(Boolean(leadData.isLastPage));

        const operatorData = operatorRes?.data?.data || [];

        const apiOperators = Array.isArray(operatorData)
          ? operatorData
          : operatorData.operators || [];

        const apiUsers =
          userRes?.data?.data?.users || userRes?.data?.data || [];

        const configData = configRes?.data?.data || {};

        const dynamicStages =
          Array.isArray(configData.leadStages) && configData.leadStages.length
            ? configData.leadStages
            : DEFAULT_STAGES;

        const dynamicStatuses =
          configData.statuses && Object.keys(configData.statuses).length
            ? configData.statuses
            : DEFAULT_STATUSES;

        setLeads(Array.isArray(apiLeads) ? apiLeads : []);
        const allLeads = allLeadRes?.data?.data?.leads || [];

        const stageWiseCounts = allLeads.reduce((acc, lead) => {
          acc[lead.currentStage] = (acc[lead.currentStage] || 0) + 1;
          return acc;
        }, {});

        setLeadCounts({
          total: allLeadRes?.data?.data?.total || allLeads.length,
          stages: stageWiseCounts,
        });
        setOperators(apiOperators);
        setStages(dynamicStages);
        setStatuses(dynamicStatuses);
        setUsers(apiUsers);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page, search, filters.currentStage, filters.status]);

  const totalLeads = leadCounts.total;

  const getStageCount = (stage) => {
    return leadCounts.stages[stage] || 0;
  };

  const handleStageTileClick = (stage) => {
    setFilters({
      currentStage: stage,
      status: "",
    });
    setPage(1);
  };

  const handleTotalTileClick = () => {
    setFilters({
      currentStage: "",
      status: "",
    });
    setPage(1);
  };

  const openCreate = () => {
    const firstStage = stages[0] || "prospecting";

    setForm({
      operatorId: "",
      currentStage: firstStage,
      status: getStatusOptions(firstStage)[0] || "",
      assignedTo: "",
    });

    setErr("");
    setFieldErr({});
    setModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setErr("");
    setFieldErr({});

    try {
      await createLead({
        operatorId: form.operatorId,
        currentStage: form.currentStage,
        status: form.status,
        assignedTo: form.assignedTo,
      });

      setModal(false);
      load();
    } catch (error) {
      setApiErrors(error, "Failed to create lead");
    }
  };

  const openStageModal = (lead) => {
    setStageForm({
      stage: lead.currentStage || stages[0] || "prospecting",
      status: lead.status || "",
      note: "",
    });

    setStageModal(lead);
    setErr("");
    setFieldErr({});
  };

  const handleStageUpdate = async (e) => {
    e.preventDefault();
    setErr("");
    setFieldErr({});

    try {
      await updateLeadStage(stageModal._id, {
        currentStage: stageForm.stage,
        status: stageForm.status,
        note: stageForm.note,
      });

      setStageModal(null);
      load();
    } catch (error) {
      setApiErrors(error, "Failed to update lead");
    }
  };

  return (
    <div style={globalStyles.contentWrapper}>
      <div style={globalStyles.row}>
        <h1 style={globalStyles.h1}>Lead Pipeline</h1>

        <button
          type="button"
          style={{
            ...globalStyles.btn,
            ...(isMobile ? { width: "100%", justifyContent: "center" } : {}),
          }}
          onClick={openCreate}
        >
          + Create Lead
        </button>
      </div>

      <div
        style={{
          ...s.tilesGrid,
          ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
        }}
      >
        <div style={isMobile ? { gridColumn: "span 1" } : s.tileGridItem(0)}>
          <LeadTile
            value={totalLeads}
            label="Total Leads"
            icon={AssignmentIcon}
            color="#6366f1"
            active={filters.currentStage === "" && filters.status === ""}
            onClick={handleTotalTileClick}
          />
        </div>

        {stages.map((stage, index) => {
          const Icon = stageIcons[stage] || AssignmentIcon;
          const color = stageColors[stage] || "#6366f1";

          return (
            <div
              key={stage}
              style={
                isMobile ? { gridColumn: "span 1" } : s.tileGridItem(index + 1)
              }
            >
              <LeadTile
                value={getStageCount(stage)}
                label={formatLabel(stage)}
                icon={Icon}
                color={color}
                active={filters.currentStage === stage}
                onClick={() => handleStageTileClick(stage)}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          ...globalStyles.toolbar,
          ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
        }}
      >
        <div
          style={{
            ...globalStyles.filterLeft,
            ...(isMobile ? globalStyles.responsive.mobileFilterLeft : {}),
          }}
        >
          <div
            style={{
              ...globalStyles.searchWrap,
              ...(isMobile ? globalStyles.responsive.mobileSearchWrap : {}),
            }}
          >
            <FiSearch style={globalStyles.searchIcon} />

            <input
              style={globalStyles.searchInput}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search leads..."
            />
          </div>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={filters.currentStage}
            onChange={(e) => {
              setFilters({
                currentStage: e.target.value,
                status: "",
              });
              setPage(1);
            }}
          >
            <option value="">All Stages</option>
            {stages.map((stage) => (
              <option key={stage} value={stage}>
                {formatLabel(stage)}
              </option>
            ))}
          </select>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={filters.status}
            onChange={(e) => {
              setFilters({
                currentStage: filters.currentStage,
                status: e.target.value,
              });
              setPage(1);
            }}
          >
            <option value="">All Status</option>
            {allStatuses.map((status) => (
              <option key={status} value={status}>
                {formatLabel(status)}
              </option>
            ))}
          </select>
        </div>
        <span style={globalStyles.paginatePageInfo}>
          Page {page} of {totalPages}
        </span>
      </div>

      {loading ? (
        <div style={globalStyles.loading}>Loading...</div>
      ) : (
        <div style={globalStyles.tableScroll}>
          <table style={globalStyles.table}>
            <thead>
              <tr>
                <th style={globalStyles.th}>Operator</th>
                <th style={globalStyles.th}>Phone</th>
                <th style={globalStyles.th}>Business Type</th>
                <th style={globalStyles.th}>Stage</th>
                <th style={globalStyles.th}>Status</th>
                <th style={globalStyles.th}>Assigned To</th>
                <th style={globalStyles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {leads.length > 0 ? (
                leads.map((lead) => (
                  <tr key={lead._id}>
                    <td style={globalStyles.td}>
                      <strong>{lead.operatorId?.name || "—"}</strong>
                    </td>

                    <td style={globalStyles.td}>
                      {lead.operatorId?.phone || "—"}
                    </td>

                    <td style={globalStyles.td}>
                      {lead.operatorId?.businessType || "—"}
                    </td>

                    <td style={globalStyles.td}>
                      <span style={globalStyles.stageBadge(lead.currentStage)}>
                        {formatLabel(lead.currentStage)}
                      </span>
                    </td>

                    <td style={globalStyles.td}>
                      <span style={globalStyles.statusBadge(lead.status)}>
                        {formatLabel(lead.status)}
                      </span>
                    </td>

                    <td style={globalStyles.td}>
                      {getPersonName(lead.assignedTo)}
                    </td>

                    <td style={globalStyles.td}>
                      <div style={globalStyles.btnRow}>
                        <button
                          type="button"
                          title="View"
                          style={globalStyles.viewBtn}
                          onClick={() => {
                            setView(lead);
                            setShowAllTimeline(false);
                          }}
                        >
                          <FiEye size={14} />
                        </button>

                        <button
                          type="button"
                          title="Move Stage"
                          style={globalStyles.editBtn}
                          onClick={() => openStageModal(lead)}
                        >
                          <FiEdit size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                  >
                    No Leads Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div style={globalStyles.paginateRow}>
          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            {"<<"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            {"<"}
          </button>

          {getPageNumbers(page, totalPages).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                background: page === pageNumber ? PRIMARY_COLOR : "#f8fafc",
                color: page === pageNumber ? "#fff" : PRIMARY_COLOR,
                minWidth: 36,
              }}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {">"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage(totalPages)}
          >
            {">>"}
          </button>
        </div>
      )}

      {modal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <div style={globalStyles.modalHeader}>
              <h2 style={globalStyles.modalTitle}>Create Lead</h2>

              <button
                type="button"
                style={globalStyles.modalCloseBtn}
                onClick={() => setModal(false)}
              >
                ✕
              </button>
            </div>

            {err && <div style={globalStyles.errorText}>{err}</div>}

            <form onSubmit={handleCreate}>
              <label style={globalStyles.label}>Operator *</label>
              <select
                style={getInputStyle("operatorId")}
                value={form.operatorId}
                onChange={(e) => {
                  setForm({
                    ...form,
                    operatorId: e.target.value,
                  });
                  clearFieldError("operatorId");
                  setErr("");
                }}
              >
                <option value="">Select Operator</option>

                {operators.map((operator) => {
                  const id = operator._id || operator.id;

                  return (
                    <option key={id} value={id}>
                      {operator.name || "No Name"}{" "}
                      {operator.phone ? `(${operator.phone})` : ""}
                    </option>
                  );
                })}
              </select>
              {getFieldError("operatorId") && (
                <div style={globalStyles.errorText}>
                  {getFieldError("operatorId")}
                </div>
              )}

              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Current Stage *</label>
                  <select
                    style={getInputStyle("currentStage")}
                    value={form.currentStage}
                    onChange={(e) => {
                      const selectedStage = e.target.value;

                      setForm({
                        ...form,
                        currentStage: selectedStage,
                        status: getStatusOptions(selectedStage)[0] || "",
                      });

                      clearFieldError("currentStage");
                      clearFieldError("status");
                      setErr("");
                    }}
                  >
                    <option value="">Select Stage</option>
                    {stages.map((stage) => (
                      <option key={stage} value={stage}>
                        {formatLabel(stage)}
                      </option>
                    ))}
                  </select>
                  {getFieldError("currentStage") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("currentStage")}
                    </div>
                  )}
                </div>

                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Status *</label>
                  <select
                    style={getInputStyle("status")}
                    value={form.status}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        status: e.target.value,
                      });
                      clearFieldError("status");
                      setErr("");
                    }}
                  >
                    <option value="">Select Status</option>
                    {getStatusOptions(form.currentStage).map((status) => (
                      <option key={status} value={status}>
                        {formatLabel(status)}
                      </option>
                    ))}
                  </select>
                  {getFieldError("status") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("status")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Assigned To *</label>
                  <select
                    style={getInputStyle("assignedTo")}
                    value={form.assignedTo}
                    onChange={(e) => {
                      setForm({ ...form, assignedTo: e.target.value });
                      clearFieldError("assignedTo");
                      setErr("");
                    }}
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name} {user.role ? `— ${user.role}` : ""}
                      </option>
                    ))}
                  </select>
                  {getFieldError("assignedTo") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("assignedTo")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {stageModal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <div style={globalStyles.modalHeader}>
              <h2 style={globalStyles.modalTitle}>Update Lead Stage</h2>

              <button
                type="button"
                style={globalStyles.modalCloseBtn}
                onClick={() => setStageModal(null)}
              >
                ✕
              </button>
            </div>

            {err && <div style={globalStyles.errorText}>{err}</div>}

            <form onSubmit={handleStageUpdate}>
              <div
                style={{
                  ...globalStyles.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Stage *</label>
                  <select
                    style={{
                      ...globalStyles.select,
                      ...(getFieldError("stage") ||
                      getFieldError("currentStage")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    value={stageForm.stage}
                    onChange={(e) => {
                      const selectedStage = e.target.value;

                      setStageForm({
                        ...stageForm,
                        stage: selectedStage,
                        status: getStatusOptions(selectedStage)[0] || "",
                      });

                      clearFieldError("stage");
                      clearFieldError("status");
                      setErr("");
                    }}
                  >
                    <option value="">Select Stage</option>
                    {stages.map((stage) => (
                      <option key={stage} value={stage}>
                        {formatLabel(stage)}
                      </option>
                    ))}
                  </select>
                  {(getFieldError("stage") ||
                    getFieldError("currentStage")) && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("stage") || getFieldError("currentStage")}
                    </div>
                  )}
                </div>

                <div style={globalStyles.half}>
                  <label style={globalStyles.label}>Status *</label>
                  <select
                    style={getInputStyle("status")}
                    value={stageForm.status}
                    onChange={(e) => {
                      setStageForm({
                        ...stageForm,
                        status: e.target.value,
                      });
                      clearFieldError("status");
                      setErr("");
                    }}
                  >
                    <option value="">Select Status</option>
                    {getStatusOptions(stageForm.stage).map((status) => (
                      <option key={status} value={status}>
                        {formatLabel(status)}
                      </option>
                    ))}
                  </select>
                  {getFieldError("status") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("status")}
                    </div>
                  )}
                </div>
              </div>

              <label style={globalStyles.label}>Note</label>
              <input
                style={globalStyles.input}
                value={stageForm.note}
                onChange={(e) =>
                  setStageForm({ ...stageForm, note: e.target.value })
                }
                placeholder="Optional note"
              />

              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setStageModal(null)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {view &&
        (() => {
          const timelineItems = getTimelineItems(view);
          const visibleTimeline = showAllTimeline
            ? timelineItems
            : timelineItems.slice(0, 3);

          return (
            <div style={globalStyles.modal}>
              <div
                style={{
                  ...globalStyles.mcard,
                  ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
                }}
              >
                <div style={globalStyles.modalHeader}>
                  <h2 style={globalStyles.modalTitle}>View Lead</h2>

                  <button
                    type="button"
                    style={globalStyles.modalCloseBtn}
                    onClick={() => setView(null)}
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    ...globalStyles.viewGrid,
                    ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
                  }}
                >
                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Operator</div>
                    <div style={globalStyles.viewValue}>
                      {view.operatorId?.name || "—"}
                    </div>
                  </div>

                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Phone</div>
                    <div style={globalStyles.viewValue}>
                      {view.operatorId?.phone || "—"}
                    </div>
                  </div>

                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Business Type</div>
                    <div style={globalStyles.viewValue}>
                      {view.operatorId?.businessType || "—"}
                    </div>
                  </div>

                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Current Stage</div>
                    <div style={globalStyles.viewValue}>
                      {formatLabel(view.currentStage)}
                    </div>
                  </div>

                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Status</div>
                    <div style={globalStyles.viewValue}>
                      {formatLabel(view.status)}
                    </div>
                  </div>

                  <div style={globalStyles.viewItem}>
                    <div style={globalStyles.viewLabel}>Assigned To</div>
                    <div style={globalStyles.viewValue}>
                      {getPersonName(view.assignedTo)}
                    </div>
                  </div>

                  <div style={globalStyles.viewItemFull}>
                    <div style={globalStyles.viewLabel}>
                      Timeline Details ({timelineItems.length})
                    </div>

                    {visibleTimeline.length > 0 ? (
                      <>
                        {visibleTimeline.map((item, index) => (
                          <div key={item._id || index} style={s.timelineItem}>
                            <div style={globalStyles.viewValue}>
                              {formatLabel(item.stage)} -{" "}
                              {formatLabel(item.status)}
                            </div>

                            <div style={s.timelineText}>
                              Note: {item.note || "—"}
                            </div>

                            <div style={s.timelineText}>
                              Updated By: {item.updatedBy || "—"}
                            </div>

                            <div style={s.timelineText}>
                              Date:{" "}
                              {item.timestamp
                                ? new Date(item.timestamp).toLocaleString()
                                : "—"}
                            </div>
                          </div>
                        ))}

                        {timelineItems.length > 3 && (
                          <button
                            type="button"
                            style={s.seeMoreBtn}
                            onClick={() => setShowAllTimeline((prev) => !prev)}
                          >
                            {showAllTimeline
                              ? "Show Less"
                              : `See More (${timelineItems.length - 3})`}
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={globalStyles.viewValue}>
                        No timeline available
                      </div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    ...globalStyles.mrow,
                    ...(isMobile ? { justifyContent: "stretch" } : {}),
                  }}
                >
                  <button
                    type="button"
                    style={{
                      ...globalStyles.cancel,
                      ...(isMobile ? { width: "100%" } : {}),
                    }}
                    onClick={() => setView(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
